import React, { useState, useEffect, useMemo } from "react";
import { Row, Col, Modal, Button, Form, Spinner } from "react-bootstrap";
import {
  Tools,
  FuelPump,
  Truck,
  CardChecklist,
  Airplane,
  Wallet2,
  ArrowRight,
  GeoAltFill,
  CheckCircleFill,
  ClockFill,
  PersonFill,
  CalendarEventFill,
  CarFrontFill,
  ClockHistory,
  XLg
} from "react-bootstrap-icons";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import axiosInstance from "../../../services/axios.instance";
import { API_URL } from "../../../services/apiConfig";
import ServiceDateTimePicker from "./ServiceDateTimePicker";
import LocationPickerModal from "../../LocationPickerModal";
import { toast } from "react-toastify";
import "./styles.css";

interface KirkosService {
  id: number;
  serviceName: string;
  status: boolean;
  type: string;
  leadTimeInMinutes: number;
}

const KirkosServices = () => {
  const [showModal, setShowModal] = useState(false);
  const [selectedService, setSelectedService] = useState<KirkosService | null>(null);
  const [showMapModal, setShowMapModal] = useState(false);
  const [mapPickerType, setMapPickerType] = useState<"pickup" | "dropoff">("pickup");
  const [profile, setProfile] = useState<any>(null);

  // Form State
  const [formData, setFormData] = useState({
    firstName: "",
    phoneNumber: "",
    scheduledFor: "",
    scheduledTime: "",
    pickupLocation: {
      address: "",
      latitude: 0,
      longitude: 0
    },
    dropOffLocation: {
      address: "",
      latitude: 0,
      longitude: 0
    },
    carModel: "",
    vehicleMake: "",
    regNumber: "",
    terminal: ""
  });

  // Fetch Active Services Catalog
  const { data: services, isLoading: isLoadingServices } = useQuery<KirkosService[]>({
    queryKey: ["kirkosServices"],
    queryFn: async () => {
      const response = await axiosInstance.get(API_URL.KIRKOS_SERVICES_ACTIVE);
      return response.data;
    }
  });

  // Fetch Consumer's Booked Services
  const { data: consumerServices, isLoading: isLoadingHistory } = useQuery<any[]>({
    queryKey: ["consumerServices"],
    queryFn: async () => {
      const response = await axiosInstance.get(`${API_URL.KIRKOS_CONSUMER_SERVICES}?status=ALL`);
      return response.data;
    }
  });

  // Fetch Profile Data for pre-filling
  useQuery({
    queryKey: ["customerDetails"],
    queryFn: async () => {
      const response = await axiosInstance.get(API_URL.CUSTOMER_DETAILS);
      const details = response?.data?.customer_details;
      if (details) {
        setProfile(details);
        setFormData(prev => ({
          ...prev,
          firstName: `${details.firstName || ""} ${details.lastName || ""}`.trim(),
          phoneNumber: details.phone_no || ""
        }));
      }
      return response.data;
    }
  });

  const queryClient = useQueryClient();

  // Create Service Order Mutation
  const createOrderMutation = useMutation({
    mutationFn: async (payload: any) => {
      return await axiosInstance.post(API_URL.KIRKOS_SERVICE_CREATE, payload);
    },
    onSuccess: () => {
      toast.success("Service request submitted successfully!");
      queryClient.invalidateQueries({ queryKey: ["consumerServices"] });
      handleClose();
    },
    onError: (error: any) => {
      toast.error(error?.response?.data?.message || "Failed to submit service request.");
    }
  });

  const resetForm = () => {
    setFormData({
      firstName: profile ? `${profile.firstName || ""} ${profile.lastName || ""}`.trim() : "",
      phoneNumber: profile?.phone_no || "",
      scheduledFor: "",
      scheduledTime: "",
      pickupLocation: {
        address: "",
        latitude: 0,
        longitude: 0
      },
      dropOffLocation: {
        address: "",
        latitude: 0,
        longitude: 0
      },
      carModel: "",
      vehicleMake: "",
      regNumber: "",
      terminal: ""
    });
    setSelectedService(null);
  };

  const handleClose = () => {
    setShowModal(false);
    resetForm();
  };

  const isRegistrationService = useMemo(() => {
    return selectedService?.type?.includes("REGISTRATION") || selectedService?.serviceName?.toLowerCase().includes("registration");
  }, [selectedService]);

  const needsDropOffLocation = useMemo(() => {
    const type = selectedService?.type;
    return type === "VEHICLE_ASSIST" || type === "FINANCIAL" || type === "TRAVEL";
  }, [selectedService]);

  const isTravelService = useMemo(() => {
    return selectedService?.type === "TRAVEL";
  }, [selectedService]);

  // Fetch RTO Locations for Registration Services
  const { data: rtoLocations, isLoading: isLoadingRtoLocations } = useQuery<any[]>({
    queryKey: ["rtoLocations", selectedService?.id],
    queryFn: async () => {
      const response = await axiosInstance.get(`${API_URL.KIRKOS_SERVICE_LOCATIONS}?type=RTO`);
      return response.data;
    },
    enabled: !!isRegistrationService && !!selectedService
  });

  // Fetch Airport Locations for Travel Services
  const { data: airportLocations, isLoading: isLoadingAirports } = useQuery<any[]>({
    queryKey: ["airportLocations", selectedService?.id],
    queryFn: async () => {
      const response = await axiosInstance.get(`${API_URL.KIRKOS_SERVICE_LOCATIONS}?type=AIRPORT`);
      return response.data;
    },
    enabled: !!isTravelService && !!selectedService
  });

  const selectedAirport = useMemo(() => {
    return airportLocations?.find(a => a.fullLocation === formData.pickupLocation.address);
  }, [airportLocations, formData.pickupLocation.address]);

  const handleServiceClick = (service: KirkosService) => {
    setSelectedService(service);
    setShowModal(true);
  };

  const handleLocationSelect = (location: { name: string; latitude: number; longitude: number }) => {
    setFormData(prev => ({
      ...prev,
      [mapPickerType === "pickup" ? "pickupLocation" : "dropOffLocation"]: {
        address: location.name,
        latitude: location.latitude,
        longitude: location.longitude
      }
    }));
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    if (!formData.scheduledFor || !formData.scheduledTime) {
      toast.error("Please select a scheduled date and time.");
      return;
    }

    if (!formData.pickupLocation.address) {
      toast.error("Please select a service location.");
      return;
    }

    if (needsDropOffLocation && !formData.dropOffLocation.address) {
      toast.error("Please select a drop-off location.");
      return;
    }

    if (isTravelService && !formData.terminal) {
      toast.error("Please select a terminal.");
      return;
    }

    const payload = {
      first_name: formData.firstName,
      phone_number: formData.phoneNumber,
      service_name: selectedService?.serviceName,
      scheduled_for: `${formData.scheduledFor}T${formData.scheduledTime}:00`,
      pickup_location: {
        address: isTravelService
          ? `${formData.pickupLocation.address} - ${formData.terminal}`
          : formData.pickupLocation.address,
        latitude: formData.pickupLocation.latitude,
        longitude: formData.pickupLocation.longitude,
        ...(isTravelService && { terminal: formData.terminal })
      },
      ...(needsDropOffLocation && {
        drop_off_location: {
          address: formData.dropOffLocation.address,
          latitude: formData.dropOffLocation.latitude,
          longitude: formData.dropOffLocation.longitude
        }
      }),
      car_model: formData.carModel,
      vehicle_make: formData.vehicleMake,
      reg_number: formData.regNumber
    };

    createOrderMutation.mutate(payload);
  };

  const getServiceIcon = (serviceName: string) => {
    const name = serviceName.toLowerCase();
    if (name.includes("battery")) return <BatteryFull size={24} />;
    if (name.includes("fuel")) return <FuelPump size={24} />;
    if (name.includes("towing")) return <Truck size={24} />;
    if (name.includes("registration")) return <CardChecklist size={24} />;
    if (name.includes("airport")) return <Airplane size={24} />;
    if (name.includes("wallet") || name.includes("financial")) return <Wallet2 size={24} />;
    return <Tools size={24} />;
  };

  const BatteryFull = ({ size }: { size: number }) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor">
      <path d="M15.67 4H14V2h-4v2H8.33C7.6 4 7 4.6 7 5.33v15.33C7 21.4 7.6 22 8.33 22h7.33c.74 0 1.34-.6 1.34-1.33V5.33C17 4.6 16.4 4 15.67 4zM11 20v-5.5H9L13 7v5.5h2L11 20z" />
    </svg>
  );

  const minDate = useMemo(() => new Date(), []);

  const minTimeStr = useMemo(() => {
    const now = new Date();
    const leadTime = selectedService?.leadTimeInMinutes || 0;
    const target = new Date(now.getTime() + leadTime * 60000);

    // If the target time is still within today, return the specific HH:mm
    if (
      target.getDate() === now.getDate() &&
      target.getMonth() === now.getMonth() &&
      target.getFullYear() === now.getFullYear()
    ) {
      const hours = target.getHours().toString().padStart(2, '0');
      const minutes = target.getMinutes().toString().padStart(2, '0');
      return `${hours}:${minutes}`;
    }
    return "23:59";
  }, [selectedService]);

  return (
    <div className="row g-4 dashboard-kirkos-container">
      <div className="col-lg-12">
        <div className="card card-custom">
          <div className="card-body">
            <div className="d-flex align-items-center gap-3 mb-3">
              <Tools className="header-icon" style={{ color: 'rgba(255, 255, 255, 0.9)' }} size={28} />
              <h1 className="page-head-add mb-0">Kirkos Premium Services</h1>
            </div>
            <p className="mb-5 text-start" style={{ color: 'var(--font-color-theme-2)', fontSize: '0.95rem', maxWidth: '700px' }}>
              Professional vehicle maintenance and documentation services delivered with excellence at your convenience.
            </p>

            {isLoadingServices ? (
              <div className="d-flex justify-content-center py-5">
                <Spinner animation="border" style={{ color: 'rgba(255, 255, 255, 0.7)' }} />
              </div>
            ) : (
              <div className="row g-4">
                {services?.map((service) => (
                  <div key={service.id} className="col-md-6 col-xl-4">
                    <div
                      className="service-card-new"
                      onClick={() => handleServiceClick(service)}
                    >
                      <div className="d-flex align-items-end gap-2 mb-4">
                        <div className="service-icon-wrapper">
                          {getServiceIcon(service.serviceName)}
                        </div>
                        <h3 className="service-title-new text-start">{service.serviceName}</h3>
                      </div>
                      <div className="d-flex justify-content-between align-items-start mb-3">
                        <div className="d-flex align-items-center gap-2" style={{ color: 'var(--font-color-theme-2)', fontSize: '0.85rem' }}>
                          <ClockFill size={14} />
                          <span>
                            Min. Notice:{" "}
                            {service.leadTimeInMinutes >= 60
                              ? `${Math.floor(service.leadTimeInMinutes / 60)}h ${service.leadTimeInMinutes % 60 > 0 ? (service.leadTimeInMinutes % 60) + 'm' : ''}`
                              : `${service.leadTimeInMinutes}m`}
                          </span>
                        </div>
                        <span className="service-badge-new">{service.type.replace("_", " ")}</span>
                      </div>
                      <div className="service-action-text border-top pt-3" style={{ borderColor: 'rgba(255,255,255,0.05) !important' }}>
                        REQUEST SERVICE <ArrowRight size={16} />
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}

            <hr className="my-5 border-secondary opacity-25" />

            <div className="d-flex align-items-center gap-3 mb-4">
              <ClockHistory className="header-icon" style={{ color: 'rgba(255, 255, 255, 0.9)' }} size={24} />
              <h2 className="page-head-add fs-4 mb-0">My Service History</h2>
            </div>

            {isLoadingHistory ? (
              <div className="d-flex justify-content-center py-4">
                <Spinner animation="border" size="sm" style={{ color: 'rgba(255, 255, 255, 0.5)' }} />
              </div>
            ) : consumerServices && consumerServices.length > 0 ? (
              <div className="service-history-list">
                {consumerServices.map((order: any) => (
                  <div key={order.orderId} className="history-item">
                    <Row className="align-items-center g-3">
                      <Col md={3}>
                        <div className="history-service-name">
                          {order.order_service_details?.serviceName || "Service Request"}
                        </div>
                        <div className="history-order-id">#{order.orderId}</div>
                      </Col>
                      <Col md={3}>
                        <div className="history-label">Vehicle Information</div>
                        <div className="history-value">
                          {order.order_service_details?.vehicleMake} {order.order_service_details?.carModel}
                        </div>
                        <div className="history-subvalue">
                          {order.order_service_details?.regNumber}
                        </div>
                      </Col>
                      <Col md={3}>
                        <div className="history-label">Scheduled For</div>
                        <div className="history-value">
                          {order.order_service_details?.scheduledFor ? new Date(order.order_service_details.scheduledFor).toLocaleDateString('en-GB', {
                            day: '2-digit',
                            month: 'short',
                            year: 'numeric'
                          }) : 'N/A'}
                        </div>
                        <div className="history-subvalue">
                          {order.order_service_details?.scheduledFor ? new Date(order.order_service_details.scheduledFor).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) : ''}
                        </div>
                      </Col>
                      <Col md={3} className="text-md-end">
                        <span className={`status-badge status-${order.orderStatus?.toLowerCase()}`}>
                          {order.orderStatus}
                        </span>
                      </Col>
                    </Row>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-5 opacity-50">
                <p>No recent service requests found.</p>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Service Request Modal */}
      <Modal
        show={showModal}
        onHide={handleClose}
        centered
        size="lg"
        scrollable
        className="service-request-modal"
        enforceFocus={false}
      >
        <Modal.Header>
          <Modal.Title>
            <div className="d-flex align-items-center gap-3">
              <div className="service-icon-wrapper mb-0" style={{ width: 40, height: 40 }}>
                {selectedService && getServiceIcon(selectedService.serviceName)}
              </div>
              <h1 className="page-head-add mb-0" style={{ fontSize: '1.25rem' }}>Request {selectedService?.serviceName}</h1>
            </div>
          </Modal.Title>
          <button className="btn btn-link text-white p-0 position-absolute end-0 me-4" onClick={handleClose}>
            <XLg size={22} />
          </button>
        </Modal.Header>

        <Modal.Body className="service-form p-4">
          <Form id="serviceRequestForm" onSubmit={handleSubmit}>
            {/* Section 1: Contact Information */}
            <div >
              <div className="d-flex align-items-center gap-2 mb-4">
                <div className="section-header-bar"></div>
                <h2 className="page-head-add fs-5 mb-0">Contact Information</h2>
              </div>
              <Row>
                <Col md={6}>
                  <Form.Group className="mb-4 dashboard-input-group">
                    <Form.Label className="dashboard-label">Full Name</Form.Label>
                    <Form.Control
                      type="text"
                      required
                      value={formData.firstName}
                      onChange={(e) => setFormData(prev => ({ ...prev, firstName: e.target.value }))}
                      placeholder="Your name"
                      className="dashboard-input"
                    />
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group className="mb-4 dashboard-input-group">
                    <Form.Label className="dashboard-label">Phone Number</Form.Label>
                    <Form.Control
                      type="text"
                      required
                      value={formData.phoneNumber}
                      onChange={(e) => setFormData(prev => ({ ...prev, phoneNumber: e.target.value }))}
                      placeholder="+971 -- --- ----"
                      className="dashboard-input"
                    />
                  </Form.Group>
                </Col>
              </Row>
            </div>

            {/* Section 2: Service Details */}
            <div className="mb-4">
              <div className="d-flex align-items-center gap-2 mb-4">
                <div className="section-header-bar"></div>
                <h2 className="page-head-add fs-5 mb-0">Schedule & Location</h2>
              </div>
              <Row>
                <Col md={12}>
                  {isRegistrationService ? (
                    <Form.Group className="dashboard-input-group">
                      <Form.Label className="dashboard-label">RTO Center Location</Form.Label>
                      <div className="position-relative">
                        <Form.Select
                          required
                          value={formData.pickupLocation.address}
                          onChange={(e) => {
                            const loc = rtoLocations?.find((l: any) => l.fullLocation === e.target.value);
                            if (loc) {
                              setFormData(prev => ({
                                ...prev,
                                pickupLocation: {
                                  address: loc.fullLocation,
                                  latitude: loc.latitude,
                                  longitude: loc.longitude
                                }
                              }));
                            }
                          }}
                          disabled={isLoadingRtoLocations}
                          className="dashboard-input dashboard-select"
                        >
                          <option value="">Select a center</option>
                          {rtoLocations?.map((loc: any) => (
                            <option key={loc.id} value={loc.fullLocation}>
                              {loc.locationName}
                            </option>
                          ))}
                        </Form.Select>
                        {isLoadingRtoLocations && (
                          <div className="position-absolute end-0 top-50 translate-middle-y me-4">
                            <Spinner animation="border" size="sm" style={{ color: 'var(--font-colour-theme-11)' }} />
                          </div>
                        )}
                      </div>
                    </Form.Group>
                  ) : isTravelService ? (
                    <>
                      <Form.Group className="dashboard-input-group mb-4">
                        <Form.Label className="dashboard-label">Select Airport</Form.Label>
                        <div className="position-relative">
                          <Form.Select
                            required
                            value={formData.pickupLocation.address}
                            onChange={(e) => {
                              const loc = airportLocations?.find((l: any) => l.fullLocation === e.target.value);
                              if (loc) {
                                setFormData(prev => ({
                                  ...prev,
                                  pickupLocation: {
                                    address: loc.fullLocation,
                                    latitude: loc.latitude,
                                    longitude: loc.longitude
                                  },
                                  terminal: "" // Reset terminal on airport change
                                }));
                              }
                            }}
                            disabled={isLoadingAirports}
                            className="dashboard-input dashboard-select"
                          >
                            <option value="">Select an airport</option>
                            {airportLocations?.map((loc: any) => (
                              <option key={loc.id} value={loc.fullLocation}>
                                {loc.locationName}
                              </option>
                            ))}
                          </Form.Select>
                          {isLoadingAirports && (
                            <div className="position-absolute end-0 top-50 translate-middle-y me-4">
                              <Spinner animation="border" size="sm" style={{ color: 'var(--font-colour-theme-11)' }} />
                            </div>
                          )}
                        </div>
                      </Form.Group>

                      <Form.Group className="dashboard-input-group mb-0">
                        <Form.Label className="dashboard-label">Select Terminal</Form.Label>
                        <Form.Select
                          required
                          value={formData.terminal}
                          onChange={(e) => setFormData(prev => ({ ...prev, terminal: e.target.value }))}
                          disabled={!formData.pickupLocation.address}
                          className="dashboard-input dashboard-select"
                        >
                          <option value="">Select terminal</option>
                          {selectedAirport?.terminals?.map((t: any) => (
                            <option key={t.id} value={t.terminalName}>
                              {t.terminalName}
                            </option>
                          ))}
                        </Form.Select>
                      </Form.Group>

                      {needsDropOffLocation && (
                        <Form.Group className="dashboard-input-group mt-4">
                          <Form.Label className="dashboard-label">Drop Off Location</Form.Label>
                          <div
                            className="dashboard-input d-flex align-items-center"
                            onClick={() => {
                              setMapPickerType("dropoff");
                              setShowMapModal(true);
                            }}
                            style={{ cursor: 'pointer', height: 'auto', minHeight: '50px' }}
                          >
                            <GeoAltFill size={18} className="me-2" style={{ color: 'var(--font-colour-theme-11)' }} />
                            <span className={`location-text ${!formData.dropOffLocation.address ? 'text-gray' : ''}`} style={{ fontSize: '1.05rem', fontWeight: 500 }}>
                              {formData.dropOffLocation.address || "Select destination from map"}
                            </span>
                          </div>
                        </Form.Group>
                      )}
                    </>
                  ) : (
                    <>
                      <Form.Group className="dashboard-input-group mb-4">
                        <Form.Label className="dashboard-label">
                          {needsDropOffLocation ? "Pick Up Location" : "Service Location"}
                        </Form.Label>
                        <div
                          className="dashboard-input d-flex align-items-center"
                          onClick={() => {
                            setMapPickerType("pickup");
                            setShowMapModal(true);
                          }}
                          style={{ cursor: 'pointer', height: 'auto', minHeight: '50px' }}
                        >
                          <GeoAltFill size={18} className="me-2" style={{ color: 'var(--font-colour-theme-11)' }} />
                          <span className={`location-text ${!formData.pickupLocation.address ? 'text-gray' : ''}`} style={{ fontSize: '1.05rem', fontWeight: 500 }}>
                            {formData.pickupLocation.address || "Select location from map"}
                          </span>
                        </div>
                      </Form.Group>

                      {needsDropOffLocation && (
                        <Form.Group className="dashboard-input-group mb-4">
                          <Form.Label className="dashboard-label">Drop Off Location</Form.Label>
                          <div
                            className="dashboard-input d-flex align-items-center"
                            onClick={() => {
                              setMapPickerType("dropoff");
                              setShowMapModal(true);
                            }}
                            style={{ cursor: 'pointer', height: 'auto', minHeight: '50px' }}
                          >
                            <GeoAltFill size={18} className="me-2" style={{ color: 'var(--font-colour-theme-11)' }} />
                            <span className={`location-text ${!formData.dropOffLocation.address ? 'text-gray' : ''}`} style={{ fontSize: '1.05rem', fontWeight: 500 }}>
                              {formData.dropOffLocation.address || "Select destination from map"}
                            </span>
                          </div>
                        </Form.Group>
                      )}
                    </>
                  )}
                </Col>
                <Col md={12}>
                  <div className="dashboard-input-group">
                    <ServiceDateTimePicker
                      label="Scheduled Date & Time"
                      dateValue={formData.scheduledFor}
                      timeValue={formData.scheduledTime}
                      onDateChange={(date) => setFormData(prev => ({ ...prev, scheduledFor: date || "" }))}
                      onTimeChange={(time) => setFormData(prev => ({ ...prev, scheduledTime: time || "" }))}
                      className="service-datetime-picker"
                      minDate={minDate}
                      minTime={minTimeStr}
                    />
                  </div>
                </Col>
              </Row>
            </div>

            {/* Section 3: Vehicle Information */}
            <div className="mb-4">
              <div className="d-flex align-items-center gap-2 mb-4">
                <div className="section-header-bar"></div>
                <h2 className="page-head-add fs-5 mb-0">Vehicle Details</h2>
              </div>
              <Row>
                <Col md={4}>
                  <Form.Group className="mb-4 dashboard-input-group">
                    <Form.Label className="dashboard-label">Make</Form.Label>
                    <Form.Control
                      type="text"
                      required
                      value={formData.vehicleMake}
                      onChange={(e) => setFormData(prev => ({ ...prev, vehicleMake: e.target.value }))}
                      placeholder="e.g. Toyota"
                      className="dashboard-input"
                    />
                  </Form.Group>
                </Col>
                <Col md={4}>
                  <Form.Group className="mb-4 dashboard-input-group">
                    <Form.Label className="dashboard-label">Model</Form.Label>
                    <Form.Control
                      type="text"
                      required
                      value={formData.carModel}
                      onChange={(e) => setFormData(prev => ({ ...prev, carModel: e.target.value }))}
                      placeholder="e.g. Camry"
                      className="dashboard-input"
                    />
                  </Form.Group>
                </Col>
                <Col md={4}>
                  <Form.Group className="mb-4 dashboard-input-group">
                    <Form.Label className="dashboard-label">Plate Number</Form.Label>
                    <Form.Control
                      type="text"
                      required
                      value={formData.regNumber}
                      onChange={(e) => setFormData(prev => ({ ...prev, regNumber: e.target.value }))}
                      placeholder="e.g. D 12345"
                      className="dashboard-input"
                    />
                  </Form.Group>
                </Col>
              </Row>
            </div>
          </Form>
        </Modal.Body>

        <Modal.Footer className="service-modal-footer">
          <Button
            className="btn all_button_style order-back_button_outline"
            onClick={handleClose}
            disabled={createOrderMutation.isPending}
          >
            CANCEL
          </Button>
          <Button
            type="submit"
            form="serviceRequestForm"
            className="btn all_button_style confirm-btn px-5"
            disabled={createOrderMutation.isPending}
          >
            {createOrderMutation.isPending ? (
              <>
                <Spinner animation="border" size="sm" className="me-2" />
                PROCESSING...
              </>
            ) : (
              "CONFIRM REQUEST"
            )}
          </Button>
        </Modal.Footer>
      </Modal>

      <LocationPickerModal
        isOpen={showMapModal}
        onClose={() => setShowMapModal(false)}
        onLocationSelect={handleLocationSelect}
        initialLocation={
          (mapPickerType === "pickup" ? formData.pickupLocation : formData.dropOffLocation).latitude !== 0 &&
            (mapPickerType === "pickup" ? formData.pickupLocation : formData.dropOffLocation).longitude !== 0
            ? {
              latitude: (mapPickerType === "pickup" ? formData.pickupLocation : formData.dropOffLocation).latitude,
              longitude: (mapPickerType === "pickup" ? formData.pickupLocation : formData.dropOffLocation).longitude,
            }
            : { latitude: 25.1972, longitude: 55.2744 }
        }
        initialAddress={mapPickerType === "pickup" ? formData.pickupLocation.address : formData.dropOffLocation.address}
        title={mapPickerType === "pickup" ? "Select Service Location" : "Select Drop-off Location"}
      />
    </div>
  );
};

export default KirkosServices;
